/**
 * Story 7: Emma State Machine Context Provider
 * 
 * Global context for Emma's contextual help system.
 * Wraps entire workflow to track state across all steps.
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useEmmaState, EmmaState, EmmaStateContext as EmmaStateContextType, EmmaMessage } from '../hooks/useEmmaState';

interface EmmaStateProviderProps {
  children: ReactNode;
}

interface EmmaStateContextValue {
  emmaState: EmmaStateContextType;
  updateEmmaState: (newState: Partial<EmmaStateContextType>) => void;
  getContextualMessage: () => EmmaMessage | null;
  dismissMessage: () => void;
  getMessageForState: (state: EmmaState, context?: Record<string, any>) => EmmaMessage;
  messageVisible: boolean;
}

const EmmaStateContext = createContext<EmmaStateContextValue | undefined>(undefined);

export const EmmaStateProvider: React.FC<EmmaStateProviderProps> = ({ children }) => {
  const emmaStateHook = useEmmaState();

  return (
    <EmmaStateContext.Provider value={emmaStateHook}>
      {children}
    </EmmaStateContext.Provider>
  );
};

export const useEmmaStateContext = () => {
  const context = useContext(EmmaStateContext);
  if (!context) {
    throw new Error('useEmmaStateContext must be used within EmmaStateProvider');
  }
  return context;
};

// Re-export useEmmaState hook for backward compatibility
export { useEmmaState } from '../hooks/useEmmaState';
export type { EmmaState, EmmaMessage } from '../hooks/useEmmaState';

export default EmmaStateContext;
